import React, { useEffect, useState } from 'react';
import './QuestionnaireCreateOverlay.css';
import { IQuestionnaireTemplate } from '../../types/questionnaire';
import questionnaireTemplatesData from '../../data/questionnaireTemplates.json';

interface QuestionnaireCreateOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectTemplate: (template: IQuestionnaireTemplate | null) => void;
}

const templates: IQuestionnaireTemplate[] = questionnaireTemplatesData as IQuestionnaireTemplate[];

// Mapping for Google Analytics button IDs
const getTemplateButtonId = (templateId: string): string => {
  const idMap: Record<string, string> = {
    'general-interview': 'btn-template-general-click',
    'technical-interview': 'btn-template-technical-click',
    'leadership-management': 'btn-template-leadership-click',
    'customer-facing-role': 'btn-template-customerfacing-click',
    'cultural-fit': 'btn-template-culturalfit-click'
  };
  return idMap[templateId] || `btn-template-${templateId}-click`;
};

const QuestionnaireCreateOverlay: React.FC<QuestionnaireCreateOverlayProps> = ({
  isOpen,
  onClose,
  onSelectTemplate
}) => {
  const [isClosing, setIsClosing] = useState(false);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      setIsClosing(false); // Reset closing state when opening
    } else {
      document.body.style.overflow = 'unset';
    }

    // Cleanup function to restore scroll when component unmounts
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        handleClose();
      }
    };

    // Add event listener when modal is open
    if (isOpen) {
      document.addEventListener('keydown', handleEscKey);
    }

    // Cleanup function to remove event listener
    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleClose = () => {
    setIsClosing(true);
    // Wait for animation to complete before actually closing
    setTimeout(() => {
      onClose();
      setIsClosing(false);
    }, 300); // Match the animation duration
  };

  if (!isOpen && !isClosing) return null;

  const handleBuildFromScratch = () => {
    onSelectTemplate(null);
  };

  const handleSelectTemplate = (template: IQuestionnaireTemplate) => {
    onSelectTemplate(template);
  };

  return (
    <>
      <div 
        className={`questionnaire-overlay-backdrop ${isClosing ? 'closing' : ''}`} 
        onClick={handleClose} 
      />
      <div className={`questionnaire-overlay-container ${isClosing ? 'closing' : ''}`}>
        <div className="questionnaire-overlay-content">
          <div className="questionnaire-overlay-header">
            <h2 className="questionnaire-overlay-title">Create New</h2>
            <button 
              className="questionnaire-overlay-close" 
              onClick={handleClose}
              aria-label="Close"
            >
              <i className="fal fa-xmark" style={{ fontSize: '1.2rem', color: '#495057' }}></i>
            </button>
          </div>
          
          <div className="questionnaire-overlay-body">
            <p className="questionnaire-overlay-subtitle">
              Start with a template for quick setup, or build from scratch.
            </p>
            
            <button 
              id="btn-template-scratch-click"
              className="build-from-scratch-button"
              onClick={handleBuildFromScratch}
            >
                <i className="fal fa-plus" style={{ fontSize: '1.2rem', marginRight: '0.4rem' }}></i>
              <span>Build from Scratch</span>
            </button>
            
            <div className="templates-section">
              <h3 className="templates-heading">Templates</h3>
              <div className="templates-grid">
                {templates.map((template) => (
                  <div 
                    key={template.id}
                    id={getTemplateButtonId(template.id)}
                    className="template-card"
                    onClick={() => handleSelectTemplate(template)}
                  >
                    <div className="template-card-header">
                      <h4 className="template-card-title">{template.title}</h4>
                      <div className="template-card-badge">
                        <img 
                          src="/svgs/video.svg" 
                          alt="Video" 
                          width="14" 
                          height="14"
                        />
                        <span style={{ fontWeight: '400' }}>Video</span>
                      </div>
                    </div>
                    <p className="template-card-description">
                      {template.description}
                    </p>
                    <p className="template-card-questions">
                      {template.questionCount} Question{template.questionCount !== 1 ? 's' : ''}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default QuestionnaireCreateOverlay;

